"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import { RefreshCw } from "lucide-react"
import { useApiClient } from "@/api/useApiClient"
import { useI18n } from "@/i18n"
import { Button } from "@/components/ui/button"

/**
 * P1 (founder-reported, 2026-09-22 — companion to the P0 server fix in
 * services/planning/src/handlers/ledger.ts's `planCoversDate`): a real plan
 * whose week has ENDED (today is past weekStartDate + 6) — distinct from
 * "nothing scheduled" (no plan/revision ever existed, EmptyState's own
 * branch, unchanged) and from a failed-regeneration attempt
 * (RegenFailedBanner, unchanged, rendered independently).
 *
 * Reuses the EXISTING regeneration mechanism verbatim — the same
 * `createPlanJob({ trigger: "regeneration" })` + `getPlanJob` poll loop
 * RegenFailedBanner.tsx already runs (same trigger value, same 15×2000ms
 * polling), never a second plan-generation path. Same single-submit
 * (`disabled={regenerating}`) and warning-banner styling as
 * RegenFailedBanner, with one addition: an explicit inline failure line so
 * a genuine request failure never leaves the button silently going quiet.
 */
export function WeekEndedBanner({ onRegenerated }: { onRegenerated: () => void }) {
  const { t } = useI18n()
  const api = useApiClient()
  const [regenerating, setRegenerating] = useState(false)
  const [failed, setFailed] = useState(false)

  async function handleRegenerate() {
    setRegenerating(true)
    setFailed(false)
    try {
      const job = await api.createPlanJob({ trigger: "regeneration" })
      for (let attempt = 0; attempt < 15; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 2000))
        try {
          const jobRes = await api.getPlanJob(job.id)
          if (jobRes.status === "completed" || jobRes.status === "failed") break
        } catch {
          // keep polling — a transient read failure shouldn't abandon the wait
        }
      }
    } catch {
      setFailed(true)
    } finally {
      setRegenerating(false)
      onRegenerated()
    }
  }

  return (
    <div
      role="status"
      data-testid="week-ended-banner"
      className="flex gap-3 rounded-bb-lg border border-gold/50 bg-gold/10 px-4 py-3 text-sm text-ink"
    >
      <RefreshCw className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
      <div>
        <p className="font-semibold">{t("views.plan.weekEndedTitle")}</p>
        <p className="mt-1 text-ink-2">{t("views.plan.weekEndedBody")}</p>
        <Button
          type="button"
          size="sm"
          className="mt-2.5 min-h-11"
          disabled={regenerating}
          onClick={() => void handleRegenerate()}
        >
          {t("views.plan.weekEndedCta")}
        </Button>
        {failed && (
          <p role="alert" className="mt-2 text-destructive">
            {t("views.plan.regenFailedBody")}
          </p>
        )}
      </div>
    </div>
  )
}
